import React, { useState, useRef, useEffect, useCallback } from 'react';
import { apiGet } from '../../lib/api-client';

interface PlayerProfile {
  playerName: string;
  totalHands: number;
  vpip: number;
  pfr: number;
  vpipPfrGap: number;
  aggressionFactor: number | null;
  wtsdPct: number;
  playerType: string;
}

const TYPE_COLORS: Record<string, string> = {
  TAG: '#22c55e',
  LAG: '#f59e0b',
  Nit: '#6366f1',
  'Calling Station': '#ef4444',
  Maniac: '#ec4899',
  Unknown: '#6b7280',
};

// In-memory cache so we don't re-fetch on every hover
const profileCache = new Map<string, PlayerProfile | null>();

export function PlayerTooltip({ name, children }: { name: string; children: React.ReactNode }) {
  const [profile, setProfile] = useState<PlayerProfile | null | undefined>(undefined);
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fetchProfile = useCallback(async () => {
    if (profileCache.has(name)) {
      setProfile(profileCache.get(name)!);
      return;
    }
    try {
      const data = await apiGet<PlayerProfile>(`/api/players/${encodeURIComponent(name)}`);
      profileCache.set(name, data);
      setProfile(data);
    } catch {
      profileCache.set(name, null);
      setProfile(null);
    }
  }, [name]);

  function handleMouseEnter() {
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
      fetchProfile();
    }, 250);
  }

  function handleMouseLeave() {
    clearTimeout(timeoutRef.current);
    setVisible(false);
    setProfile(undefined);
  }

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return (
    <span
      className="player-tooltip-wrap"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {visible && (
        <div className="player-tooltip">
          {profile === undefined ? (
            <div className="player-tooltip-loading">Loading...</div>
          ) : profile === null ? (
            <div className="player-tooltip-empty">No profile data</div>
          ) : (
            <>
              <div className="player-tooltip-header">
                <span className="player-tooltip-name">{profile.playerName}</span>
                <span
                  className="player-tooltip-type"
                  style={{ backgroundColor: TYPE_COLORS[profile.playerType] ?? '#6b7280' }}
                >
                  {profile.playerType}
                </span>
              </div>
              <div className="player-tooltip-stats">
                <div className="player-tooltip-row">
                  <span>Hands</span>
                  <span>{profile.totalHands.toLocaleString()}</span>
                </div>
                <div className="player-tooltip-row">
                  <span>VPIP</span>
                  <span>{profile.vpip.toFixed(1)}%</span>
                </div>
                <div className="player-tooltip-row">
                  <span>PFR</span>
                  <span>{profile.pfr.toFixed(1)}%</span>
                </div>
                <div className="player-tooltip-row">
                  <span>Gap</span>
                  <span>{profile.vpipPfrGap.toFixed(1)}</span>
                </div>
                <div className="player-tooltip-row">
                  <span>AF</span>
                  <span>{profile.aggressionFactor != null ? profile.aggressionFactor.toFixed(1) : 'N/A'}</span>
                </div>
                <div className="player-tooltip-row">
                  <span>WTSD</span>
                  <span>{profile.wtsdPct.toFixed(1)}%</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </span>
  );
}
